import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { isFullyBlocked, isSCBlocked, getBlockReason, getStateTier } from "../config/blockedStates";

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const GeoContext = createContext(null);

export const useGeo = () => useContext(GeoContext);

export const GeoProvider = ({ children }) => {
  const [geo, setGeo] = useState({ country: null, state: null, tier: null, reason: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await axios.get(`${API}/api/geo/check`, { timeout: 6000 });
        const state = data?.state || null;
        setGeo({
          country: data?.country || null,
          state,
          tier: data?.tier ?? getStateTier(state),
          reason: data?.reason || getBlockReason(state),
        });
      } catch {
        setGeo({ country: null, state: null, tier: null, reason: null });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const verifyAccess = async (action = "play", coinType = "sweeps", amount = 0) => {
    if (!geo?.state) return { allowed: true };
    try {
      const { data } = await axios.post(`${API}/api/geo/verify-access`, {
        state: geo.state,
        action,
        coin_type: coinType,
        amount,
      });
      return data;
    } catch {
      return { allowed: true };
    }
  };

  const value = {
    loading,
    userState: geo.state,
    country: geo.country,
    tier: geo.tier,
    blockReason: geo.reason,
    isBlocked: isFullyBlocked(geo.state),
    isSCBlocked: isSCBlocked(geo.state),
    verifyAccess,
  };

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
};

export const useGameAccess = () => {
  const geo = useGeo();
  const checkGameAccess = async (coinType = "sweeps") => {
    if (!geo) return { allowed: true };
    return await geo.verifyAccess("play", coinType);
  };
  return {
    checkGameAccess,
    isBlocked: geo?.isBlocked,
    isSCBlocked: geo?.isSCBlocked,
    userState: geo?.userState,
  };
};

export default GeoProvider;
